import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Loader2, Mail, MapPin, Phone } from "lucide-react";
import { MaskedLines, Reveal, SectionTag, EASE } from "../components/ui-bits";
import { CONTACT, FORM_ENDPOINT } from "../content";

const PROPERTY_TYPES = [
    "Office / Corporate",
    "Medical / Healthcare",
    "Hotel / Hospitality",
    "Multifamily / Residential",
    "Retail / Restaurant",
    "School / Campus",
    "Parking / Municipal",
    "Other",
];

const inputCls =
    "w-full border border-inkline bg-paper px-5 py-4 text-sm font-semibold outline-none transition-[border-color,box-shadow] duration-300 placeholder:font-normal placeholder:text-smoke/60 focus:border-bluebrand focus:shadow-[0_0_0_3px_rgb(0_85_255/0.15)]";

function Field({ label, children, htmlFor }) {
    return (
        <div>
            <label htmlFor={htmlFor} className="mb-2 block text-xs font-bold uppercase tracking-widest text-smoke">
                {label}
            </label>
            {children}
        </div>
    );
}

export default function GetStarted() {
    const [status, setStatus] = useState("idle");
    const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", propertyType: "", details: "", website: "" });

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setStatus("sending");
        try {
            const res = await fetch(FORM_ENDPOINT, {
                method: "POST",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                body: JSON.stringify(form),
            });
            const data = await res.json().catch(() => null);
            if (!res.ok || !data?.ok) throw new Error("Send failed");
            setStatus("success");
        } catch {
            setStatus("error");
        }
    };

    return (
        <div data-testid="get-started-page">
            <section className="blueprint-grid border-b border-inkline">
                <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-16 px-6 pb-20 pt-16 md:px-10 lg:grid-cols-12 lg:gap-8 lg:pb-28 lg:pt-24">
                    <div className="lg:col-span-5">
                        <SectionTag>Get started</SectionTag>
                        <MaskedLines
                            lines={["48 HOURS TO", "YOUR QUOTE."]}
                            className="font-display text-[13vw] font-bold leading-[0.9] tracking-tighter sm:text-7xl lg:text-[5.5rem]"
                            accentIndexes={[1]}
                        />
                        <Reveal delay={0.5}>
                            <p className="mt-8 max-w-md text-base leading-relaxed text-smoke md:text-lg">
                                Tell us about your building. We'll schedule a free site walk, audit your signage
                                against ADA and Title 24, and send a fixed quote within two business days.
                            </p>
                        </Reveal>

                        <div className="mt-12 space-y-px border border-inkline bg-[rgb(17_17_17/0.12)]">
                            {[
                                { n: "01", t: "Send the form", d: "Two minutes. No commitment." },
                                { n: "02", t: "Free site walk", d: "We audit every sign, on us." },
                                { n: "03", t: "Fixed quote in 48h", d: "Room-by-room schedule and pricing." },
                            ].map((s, i) => (
                                <Reveal key={s.n} delay={0.15 * i} className="flex gap-5 bg-bone p-6" data-testid={`step-${s.n}`}>
                                    <span className="font-display text-3xl font-bold tracking-tighter text-bluebrand">{s.n}</span>
                                    <div>
                                        <p className="font-display text-base font-bold tracking-tight">{s.t}</p>
                                        <p className="mt-1 text-sm text-smoke">{s.d}</p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>

                        <Reveal delay={0.2} className="mt-10 space-y-3">
                            <a href={`tel:${CONTACT.phone.replace(/[^0-9]/g, "")}`} data-testid="contact-phone" className="flex items-center gap-3 text-sm font-semibold transition-colors duration-300 hover:text-bluebrand">
                                <Phone className="h-4 w-4 text-bluebrand" /> {CONTACT.phone}
                            </a>
                            <a href={`mailto:${CONTACT.email}`} data-testid="contact-email" className="flex items-center gap-3 text-sm font-semibold transition-colors duration-300 hover:text-bluebrand">
                                <Mail className="h-4 w-4 text-bluebrand" /> {CONTACT.email}
                            </a>
                            <p className="flex items-center gap-3 text-sm font-semibold text-smoke" data-testid="contact-location">
                                <MapPin className="h-4 w-4 text-bluebrand" /> {CONTACT.location} — {CONTACT.area}
                            </p>
                        </Reveal>
                    </div>

                    <div className="lg:col-span-7">
                        <Reveal delay={0.3}>
                            <div className="relative">
                                <div className="absolute -left-4 -top-4 h-full w-full bg-volt" aria-hidden="true" />
                                <div className="relative border border-inkline bg-paper p-8 md:p-12">
                                    <AnimatePresence mode="wait">
                                        {status === "success" ? (
                                            <motion.div
                                                key="success"
                                                initial={{ opacity: 0, y: 24 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.6, ease: EASE }}
                                                className="py-16 text-center"
                                                data-testid="form-success"
                                            >
                                                <CheckCircle2 className="mx-auto h-14 w-14 text-bluebrand" strokeWidth={1.5} />
                                                <h2 className="mt-6 font-display text-3xl font-bold tracking-tight md:text-4xl">
                                                    Request received.
                                                </h2>
                                                <p className="mx-auto mt-4 max-w-sm text-sm leading-relaxed text-smoke">
                                                    We'll reply within one business day to schedule your free site walk.
                                                    Talk soon.
                                                </p>
                                            </motion.div>
                                        ) : (
                                            <motion.form
                                                key="form"
                                                onSubmit={submit}
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                className="space-y-6"
                                                data-testid="quote-form"
                                            >
                                                <h2 className="font-display text-2xl font-bold tracking-tight md:text-3xl">
                                                    Request your free audit
                                                </h2>
                                                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                                    <Field label="Full name *" htmlFor="gs-name">
                                                        <input id="gs-name" data-testid="form-name" required value={form.name} onChange={update("name")} className={inputCls} placeholder="Jordan Ramirez" />
                                                    </Field>
                                                    <Field label="Company / Property" htmlFor="gs-company">
                                                        <input id="gs-company" data-testid="form-company" value={form.company} onChange={update("company")} className={inputCls} placeholder="Sunset Plaza LP" />
                                                    </Field>
                                                    <Field label="Email *" htmlFor="gs-email">
                                                        <input id="gs-email" data-testid="form-email" type="email" required value={form.email} onChange={update("email")} className={inputCls} placeholder="you@company.com" />
                                                    </Field>
                                                    <Field label="Phone" htmlFor="gs-phone">
                                                        <input id="gs-phone" data-testid="form-phone" type="tel" value={form.phone} onChange={update("phone")} className={inputCls} placeholder="(213) 555-0100" />
                                                    </Field>
                                                </div>
                                                <Field label="Property type" htmlFor="gs-type">
                                                    <select id="gs-type" data-testid="form-property-type" value={form.propertyType} onChange={update("propertyType")} className={inputCls}>
                                                        <option value="">Select a property type</option>
                                                        {PROPERTY_TYPES.map((p) => (
                                                            <option key={p} value={p}>{p}</option>
                                                        ))}
                                                    </select>
                                                </Field>
                                                <Field label="Tell us about the project" htmlFor="gs-details">
                                                    <textarea
                                                        id="gs-details"
                                                        data-testid="form-details"
                                                        rows={5}
                                                        value={form.details}
                                                        onChange={update("details")}
                                                        className={`${inputCls} resize-none`}
                                                        placeholder="Number of floors, approximate sign count, upcoming inspection or TI, deadlines…"
                                                    />
                                                </Field>
                                                {status === "error" && (
                                                    <p className="border border-inkline bg-bone p-4 text-sm font-semibold text-ink" data-testid="form-error">
                                                        Your request couldn't be sent right now. Email us directly at{" "}
                                                        <a href={`mailto:${CONTACT.email}`} className="text-bluebrand underline">{CONTACT.email}</a>{" "}
                                                        or call {CONTACT.phone} — we'll take it from there.
                                                    </p>
                                                )}
                                                <input
                                                    type="text"
                                                    name="website"
                                                    value={form.website}
                                                    onChange={update("website")}
                                                    tabIndex={-1}
                                                    autoComplete="off"
                                                    aria-hidden="true"
                                                    className="pointer-events-none absolute -left-[9999px] h-0 w-0 opacity-0"
                                                />
                                                <button
                                                    type="submit"
                                                    data-testid="form-submit"
                                                    disabled={status === "sending"}
                                                    className="group inline-flex w-full items-center justify-center gap-3 rounded-full bg-bluebrand px-8 py-5 font-display text-sm font-bold uppercase tracking-widest text-white transition-[background-color,transform] duration-300 hover:bg-ink active:scale-[0.98] disabled:opacity-60 sm:w-auto"
                                                >
                                                    {status === "sending" ? (
                                                        <>
                                                            Sending
                                                            <Loader2 className="h-4 w-4 animate-spin" />
                                                        </>
                                                    ) : (
                                                        <>
                                                            Request free audit
                                                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                                                        </>
                                                    )}
                                                </button>
                                                <p className="text-xs text-smoke">
                                                    No spam, no obligation. Your details go straight to our estimating team.
                                                </p>
                                            </motion.form>
                                        )}
                                    </AnimatePresence>
                                </div>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>
        </div>
    );
}
